import { Injectable } from '@angular/core';
import { User } from '@app/core/interfaces';
import { UserRoles } from '@app/core/enums/userRoles.enum';
import { Amplify } from 'aws-amplify';
import {
  signOut,
  signInWithRedirect,
  getCurrentUser,
  fetchAuthSession,
  type AuthUser,
} from 'aws-amplify/auth';

/**
 * What the user is told when the bundle has no usable Google sign-in config. It means
 * the deployment is wrong, so it is shown in place of the sign-in button.
 */
export const UNCONFIGURED_MESSAGE =
  'Sign-in is not configured for this environment. Please contact support.';

/** Writable boundary adapter so tests can replace Amplify without mocking application code. */
export const authSession = {
  fetch: fetchAuthSession,
};

@Injectable({
  providedIn: 'root',
})
export class CognitoAuthService {
  getCurrentUser$(): Promise<AuthUser> {
    return getCurrentUser();
  }

  /** Whether this deployment configured the hosted domain Google signs in through. */
  isGoogleSignInAvailable(): boolean {
    return !!Amplify.getConfig().Auth?.Cognito.loginWith?.oauth;
  }

  /**
   * Hands the browser to Google through the Cognito hosted domain. It resolves as the
   * page navigates away; the session arrives on the return trip, where Amplify
   * exchanges the code before `currentUser$` yields.
   */
  signInWithGoogle$(): Promise<void> {
    return signInWithRedirect({ provider: 'Google' });
  }

  signOut$(): Promise<void> {
    return signOut();
  }

  /**
   * The id token, which is what the API validates: its `aud` is the app client id and
   * it carries the email and group claims the API maps to a user. The access token
   * carries scopes instead and has no `aud`, so it would fail audience validation.
   *
   * Amplify refreshes this from the pool when it is close to expiry, so it is read per
   * request rather than cached — see AuthorizationInterceptor.
   */
  async idToken$(): Promise<string | null> {
    try {
      const { tokens } = await authSession.fetch();
      return tokens?.idToken?.toString() ?? null;
    } catch {
      // No session, or no configured pool. Callers treat both as "not signed in".
      return null;
    }
  }

  /**
   * The signed-in user built from the id token's claims, or null when there is no
   * session. Amplify holds the tokens, so this is the single place claims are turned
   * into the app's `User` — the store and guards read it rather than decoding tokens
   * themselves.
   */
  async currentUser$(): Promise<User | null> {
    try {
      const { tokens } = await authSession.fetch();
      const payload = tokens?.idToken?.payload;
      if (!payload?.['email']) {
        return null;
      }

      const groups = payload['cognito:groups'];
      const roles = Array.isArray(groups) && groups.length
        ? groups.map(String)
        : [UserRoles.user];
      return {
        id: payload.sub,
        email: String(payload['email']),
        firstName: (payload['given_name'] as string) ?? null,
        lastName: (payload['family_name'] as string) ?? null,
        // Preserve the legacy fallback for accounts created before group provisioning.
        // New verified signups receive Employee from the PostConfirmation trigger, while
        // Admin remains opt-in through explicit Cognito group membership.
        role: roles[0],
        roles,
      };
    } catch {
      return null;
    }
  }
}
